import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PDF } from 'constants/businessToolSections';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { useBusinessToolSessionStore } from 'store/useBusinessToolStore';
import { getBusinessToolMode } from 'store/useBusinessToolStore/selectors';

import { checkIsPreviewMode } from 'pages/BusinessToolPage/utils';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { SidebarNavigation } from '../../components/SidebarNavigation';

export const ForBankSectionsNavigation: FC = () => {
  const { t } = useTranslation();
  const businessToolMode = useBusinessToolSessionStore(getBusinessToolMode);
  const isPreviewMode = checkIsPreviewMode(businessToolMode);

  const titlePage = {
    label: t(`chapters.${PDF.TITLE_PAGE_AND_TABLE_OF_CONTENTS}`),
    name: PDF.TITLE_PAGE_AND_TABLE_OF_CONTENTS,
    disabled: !useCheckSubSectionAccess(PDF.TITLE_PAGE_AND_TABLE_OF_CONTENTS),
  };
  const overview = {
    label: t(`chapters.${FOR_BANK_SECTIONS.OVERVIEW}`),
    name: FOR_BANK_SECTIONS.OVERVIEW,
    disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.OVERVIEW),
    items: [
      {
        id: FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.COMPANY,
        label: t('overview.company.title'),
      },
      {
        id: FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.PROJECT,
        label: t('overview.project.title'),
      },
      {
        id: FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.MARKET_CUSTOMERS,
        label: t('overview.market.title'),
      },
      {
        id: FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.FINANCIAL_INFO,
        label: t('overview.financialInformation.title'),
      },
      {
        id: FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.FUNDING_NEEDS,
        label: t('overview.fundingNeeds.title'),
      },
    ],
  };

  const options = [
    ...(isPreviewMode ? [titlePage] : [overview]),
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.EXECUTIVE_SUMMARY}`),
      name: FOR_BANK_SECTIONS.EXECUTIVE_SUMMARY,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.EXECUTIVE_SUMMARY),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.GOALS_AND_OBJECTIVES,
          label: t('executiveSummary.goalsAndObjectives.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.COMPANY_DESCRIPTION,
          label: t('executiveSummary.companyDescription.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.PRODUCTS_OR_SERVICES,
          label: t('executiveSummary.products.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.MARKET_AND_CUSTOMERS,
          label: t('executiveSummary.market.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.ORGANIZATION_AND_TEAM,
          label: t('executiveSummary.team.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.FINANCIAL_INFORMATION,
          label: t('executiveSummary.financialInformation.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.FINANCING_NEEDS,
          label: t('executiveSummary.financialNeeds.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_EXECUTIVE_SUMMARY.RISKS,
          label: t('executiveSummary.risks.title'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.ABOUT_COMPANY}`),
      name: FOR_BANK_SECTIONS.ABOUT_COMPANY,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.ABOUT_COMPANY),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_ABOUT_COMPANY.GENERAL_INFO,
          label: t('generalInfo'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_ABOUT_COMPANY.COMPANY_HISTORY,
          label: t('companyHistory'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_ABOUT_COMPANY.MISSION_AND_STRATEGY,
          label: t('missionVisionStrategy'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_ABOUT_COMPANY.SWOT,
          label: t('swotAnalysis'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES}`),
      name: FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_PRODUCTS_AND_SERVICES.DESCRIPTION,
          label: t('productsAndServicesForm.description.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_PRODUCTS_AND_SERVICES.ADVANTAGES,
          label: t('productsAndServicesForm.uniqueness.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_PRODUCTS_AND_SERVICES.R_AND_D,
          label: t('productsAndServicesForm.researchAndDevelopment.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_PRODUCTS_AND_SERVICES.DELIVERY_AND_SUPPORT,
          label: t('productsAndServicesForm.deliveryAndSupport.title'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.MARKET_ANALYSIS}`),
      name: FOR_BANK_SECTIONS.MARKET_ANALYSIS,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.MARKET_ANALYSIS),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKET_ANALYSIS.MARKET_OVERVIEW,
          label: t('marketAnalysisForm.overview.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKET_ANALYSIS.COMPETITIVE_ANALYSIS,
          label: t('marketAnalysisForm.competitors.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKET_ANALYSIS.CONSUMER_NEEDS,
          label: t('marketAnalysisForm.consumers.title'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.MARKETING_AND_SALES}`),
      name: FOR_BANK_SECTIONS.MARKETING_AND_SALES,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.MARKETING_AND_SALES),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
          label: t('marketingAndSales.sales.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.MARKETING_PLAN,
          label: t('marketingAndSales.marketing.title'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.BUSINESS_OPERATIONS}`),
      name: FOR_BANK_SECTIONS.BUSINESS_OPERATIONS,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.BUSINESS_OPERATIONS),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.LOCATION_AND_PREMISES,
          label: t('businessOperations.location.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.EQUIPMENT,
          label: t('businessOperations.equipment.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.ASSETS,
          label: t('businessOperations.assets.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.TECHNOLOGICAL_PROCESS,
          label: t('businessOperations.technologicalProcess.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.WORKING_RESOURCES,
          label: t('businessOperations.workingResources.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.PARTNERS,
          label: t('businessOperations.partners.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.PRODUCTION,
          label: t('businessOperations.production.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.QUALITY,
          label: t('businessOperations.quality.title'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION}`),
      name: FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MANAGEMENT_AND_ORGANIZATION.MANAGEMENT_TEAM,
          label: t('management.team.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MANAGEMENT_AND_ORGANIZATION.EXTERNAL_PARTNERS_AND_ADVISERS,
          label: t('management.partners.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION,
          label: t('management.staff.title'),
        },
      ],
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.IMPLEMENTATION_PLAN}`),
      name: FOR_BANK_SECTIONS.IMPLEMENTATION_PLAN,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.IMPLEMENTATION_PLAN),
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.RAISING_AND_ALLOCATING_FUNDS}`),
      name: FOR_BANK_SECTIONS.RAISING_AND_ALLOCATING_FUNDS,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.RAISING_AND_ALLOCATING_FUNDS),
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS}`),
      name: FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS),
    },
    {
      label: t(`chapters.${FOR_BANK_SECTIONS.FINANCIAL_PLAN}`),
      name: FOR_BANK_SECTIONS.FINANCIAL_PLAN,
      disabled: !useCheckSubSectionAccess(FOR_BANK_SECTIONS.FINANCIAL_PLAN),
      items: isPreviewMode ? [] : [
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.KEY_ASSUMPTIONS,
          label: t('financialPlan.keyAssumptions.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.REVENUE_PLAN,
          label: t('financialPlan.revenuePlan.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PLANNED_EXPENSES,
          label: t('financialPlan.plannedExpenses.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROFIT_AND_LOSS_PROJECTION,
          label: t('financialPlan.profitAndLossProjection.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.CASH_FLOW_ELEMENTS,
          label: t('financialPlan.cashFlowElements.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROJECTED_CASH_FLOW,
          label: t('financialPlan.projectedCashFlow.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.OPENING_BALANCE_SHEETS,
          label: t('financialPlan.openingBalanceSheets.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROJECTED_BALANCE_SHEETS,
          label: t('financialPlan.projectedBalanceSheets.title'),
        },
        {
          id: FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.FINANCIAL_PERFORMANCE,
          label: t('financialPlan.financialPerformance.title'),
        },
      ],
    },
  ];

  return <SidebarNavigation options={options} />;
};
